import React, { useState, useEffect, useCallback } from 'react'; 
import { 
  Plus, 
  Pill, 
  Edit3, 
  Trash2, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  User,
  Package,
  X,
  Save
} from 'lucide-react';
import { pacienteMedicamentoService } from '../services';
import SeletorMedicamentos from './SeletorMedicamentos';

const PacienteMedicamentos = ({ pacienteId }) => {
  const [medicamentosVinculados, setMedicamentosVinculados] = useState([]);
  const [estatisticas, setEstatisticas] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalMedicamentos, setShowModalMedicamentos] = useState(false);
  const [showModalExclusao, setShowModalExclusao] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' ou 'edit'
  const [vinculacaoEditando, setVinculacaoEditando] = useState(null);
  const [vinculacaoParaExcluir, setVinculacaoParaExcluir] = useState(null);
  const [medicamentoSelecionado, setMedicamentoSelecionado] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    quantidade_mensal: '',
    dosagem_prescrita: '',
    observacoes: '',
    tipo_retirada: 'mensal',
    frequencia_semanas: '',
    dia_retirada: '',
    intervalo_meses: '1'
  });

  const carregarMedicamentosVinculados = useCallback(async () => {
    try {
      setLoading(true);
      const data = await pacienteMedicamentoService.getByPaciente(pacienteId);
      const medicamentosAtivos = (data.medicamentosVinculados || []).filter(
        vinculacao => vinculacao.ativo !== false
      );
      setMedicamentosVinculados(medicamentosAtivos);
      setEstatisticas(data.estatisticas || {});
    } catch (error) {
      console.error('Erro ao carregar medicamentos vinculados:', error);
      showMessage('error', 'Erro ao carregar medicamentos vinculados');
    } finally {
      setLoading(false);
    }
  }, [pacienteId]);

  useEffect(() => {
    carregarMedicamentosVinculados();
  }, [carregarMedicamentosVinculados]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const abrirModalAdicionar = () => {
    setModalMode('add');
    setMedicamentoSelecionado(null);
    setFormData({
      quantidade_mensal: '',
      dosagem_prescrita: '',
      observacoes: '',
      tipo_retirada: 'mensal',
      frequencia_semanas: '',
      dia_retirada: '',
      intervalo_meses: '1'
    });
    setShowModal(true);
  };

  const abrirModalMedicamentos = () => {
    setShowModalMedicamentos(true);
  };

  const selecionarMedicamento = (medicamento) => {
    setMedicamentoSelecionado(medicamento);
    setShowModalMedicamentos(false);
  };

  const abrirModalEditar = (vinculacao) => {
    setModalMode('edit');
    setVinculacaoEditando(vinculacao);
    setMedicamentoSelecionado(vinculacao.medicamentoRegistrado);
    setFormData({
      quantidade_mensal: vinculacao.quantidade_mensal,
      dosagem_prescrita: vinculacao.dosagem_prescrita || '',
      observacoes: vinculacao.observacoes || '',
      tipo_retirada: vinculacao.tipo_retirada || 'mensal',
      frequencia_semanas: vinculacao.frequencia_semanas || '',
      dia_retirada: vinculacao.dia_retirada || '',
      intervalo_meses: vinculacao.intervalo_meses || '1'
    });
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setVinculacaoEditando(null);
    setMedicamentoSelecionado(null);
    setFormData({
      quantidade_mensal: '',
      dosagem_prescrita: '',
      observacoes: '',
      tipo_retirada: 'mensal',
      frequencia_semanas: '',
      dia_retirada: '',
      intervalo_meses: '1'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (modalMode === 'add' && !medicamentoSelecionado) {
      showMessage('error', 'Selecione um medicamento');
      return;
    }

    if (!formData.quantidade_mensal) {
      showMessage('error', 'Quantidade mensal é obrigatória');
      return;
    }

    // Validação específica por tipo de retirada
    if (formData.tipo_retirada === 'semanal') {
      if (!formData.frequencia_semanas) {
        showMessage('error', 'Frequência de semanas é obrigatória');
        return;
      }
    } else if (formData.tipo_retirada === 'mensal') {
      if (!formData.dia_retirada) {
        showMessage('error', 'Dia da retirada é obrigatório');
        return;
      }
      if (!formData.intervalo_meses) {
        showMessage('error', 'Frequência de meses é obrigatória');
        return;
      }
    }

    try {
      const vinculacaoData = {
        ...formData,
        paciente_id: pacienteId,
        quantidade_mensal: parseFloat(formData.quantidade_mensal),
        frequencia_semanas: formData.tipo_retirada === 'semanal' ? parseInt(formData.frequencia_semanas) : null,
        dia_retirada: formData.tipo_retirada === 'mensal' ? parseInt(formData.dia_retirada) : null,
        intervalo_meses: formData.tipo_retirada === 'mensal' ? parseInt(formData.intervalo_meses) : null
      };

      if (modalMode === 'add') {
        vinculacaoData.medicamento_registrado_id = medicamentoSelecionado.id;
        await pacienteMedicamentoService.create(vinculacaoData);
        showMessage('success', 'Medicamento vinculado com sucesso!');
      } else {
        await pacienteMedicamentoService.update(vinculacaoEditando.id, vinculacaoData);
        showMessage('success', 'Vinculação atualizada com sucesso!');
      }

      fecharModal();
      carregarMedicamentosVinculados();
    } catch (error) {
      console.error('Erro ao salvar vinculação:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao salvar vinculação';
      showMessage('error', errorMessage);
    }
  };

  const abrirModalExclusao = (vinculacao) => {
    setVinculacaoParaExcluir(vinculacao);
    setShowModalExclusao(true);
  };

  const fecharModalExclusao = () => {
    setShowModalExclusao(false);
    setVinculacaoParaExcluir(null);
  };

  const confirmarExclusao = async () => {
    if (!vinculacaoParaExcluir) return;

    try {
      await pacienteMedicamentoService.delete(vinculacaoParaExcluir.id);
      showMessage('success', 'Medicamento removido com sucesso!');
      carregarMedicamentosVinculados();
      fecharModalExclusao();
    } catch (error) {
      console.error('Erro ao remover vinculação:', error);
      showMessage('error', 'Erro ao remover medicamento');
    }
  };

  const getCorControle = (tipoControle) => {
    switch (tipoControle) {
      case 'especial': return '#ff6b6b';
      case 'normal': return '#ffa726';
      default: return '#28a745';
    }
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <div></div>
        
        <button 
          onClick={abrirModalAdicionar}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Plus size={18} />
          Vincular Medicamento
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.type === 'success' && <CheckCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* Estatísticas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '15px',
        marginBottom: '25px'
      }}>
        <div className="stat-card">
          <div className="stat-number" style={{ color: '#007bff' }}>
            {estatisticas.totalVinculados || 0}
          </div>
          <div className="stat-label">Medicamentos Ativos</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-number" style={{ color: '#ffa726' }}>
            {estatisticas.medicamentosControlados || 0}
          </div>
          <div className="stat-label">Controlados</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-number" style={{ color: '#28a745' }}>
            {estatisticas.quantidadeMensalTotal || 0}
          </div>
          <div className="stat-label">Unidades/Mês</div>
        </div>
      </div>

      {/* Lista de Medicamentos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Carregando medicamentos...</p>
        </div>
      ) : medicamentosVinculados.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <Pill size={48} color="#6c757d" style={{ marginBottom: '15px' }} />
          <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>Nenhum medicamento vinculado</h4>
          <p style={{ color: '#6c757d' }}>
            Clique em "Vincular Medicamento" para adicionar medicamentos a este paciente.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {medicamentosVinculados.map(vinculacao => (
            <div key={vinculacao.id} className="medicamento-vinculado-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, color: '#333' }}>
                      {vinculacao.medicamentoRegistrado.nome}
                    </h4>
                    <span 
                      className="badge"
                      style={{ 
                        backgroundColor: getCorControle(vinculacao.medicamentoRegistrado.tipoControle),
                        color: 'white',
                        fontSize: '12px',
                        padding: '4px 8px'
                      }}
                    >
                      {vinculacao.medicamentoRegistrado.tipoControle === 'especial' ? 'Especial' :
                       vinculacao.medicamentoRegistrado.tipoControle === 'normal' ? 'Normal' : 'Sem Controle'}
                    </span>
                  </div>
                  
                  <p style={{ margin: '0 0 8px 0', color: '#667' }}>
                    {vinculacao.medicamentoRegistrado.principioAtivo}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <span className="info-item">
                      <Package size={16} />
                      Qtd. Mensal: <strong>{vinculacao.quantidade_mensal} un.</strong>
                    </span>
                    
                    {vinculacao.dosagem_prescrita && (
                      <span className="info-item">
                        <Pill size={16} />
                        Dosagem: <strong>{vinculacao.dosagem_prescrita}</strong>
                      </span>
                    )}
                    
                    {vinculacao.tipo_retirada === 'semanal' && vinculacao.frequencia_semanas && (
                      <span className="info-item">
                        <Calendar size={16} />
                        Frequência: <strong>A cada {vinculacao.frequencia_semanas} semana{vinculacao.frequencia_semanas > 1 ? 's' : ''}</strong>
                      </span>
                    )}
                    
                    {vinculacao.tipo_retirada === 'mensal' && (
                      <>
                        {vinculacao.dia_retirada && (
                          <span className="info-item">
                            <Calendar size={16} />
                            Dia da retirada: <strong>Dia {vinculacao.dia_retirada}</strong>
                          </span>
                        )}
                        
                        {vinculacao.intervalo_meses && (
                          <span className="info-item">
                            <Calendar size={16} />
                            Intervalo: <strong>{vinculacao.intervalo_meses === 1 ? 'Mensal' : `A cada ${vinculacao.intervalo_meses} meses`}</strong>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  
                    <span className="info-item" style={{ fontSize: '14px', color: '#667' }}>
                      <User size={14} />
                      Responsável: {vinculacao.usuarioResponsavel.nome}
                    </span>
                  
                  {vinculacao.observacoes && (
                    <div style={{ 
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      <strong>Observações:</strong> {vinculacao.observacoes}
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => abrirModalEditar(vinculacao)}
                    className="btn btn-sm btn-outline"
                    title="Editar vinculação"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => abrirModalExclusao(vinculacao)}
                    className="btn btn-sm btn-danger"
                    title="Remover vinculação"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para Adicionar/Editar Vinculação */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>
                {modalMode === 'add' ? 'Vincular Medicamento' : 'Editar Vinculação'}
              </h3>
              <button onClick={fecharModal} className="modal-close">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-body">
              {/* Seleção de Medicamento */}
              {modalMode === 'add' && (
                <div className="form-group">
                  <label>Medicamento *</label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '12px', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {medicamentoSelecionado ? (
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', color: '#495057' }}>
                          {medicamentoSelecionado.nome}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6c757d' }}>
                          {medicamentoSelecionado.principioAtivo} - {medicamentoSelecionado.fabricante}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          Estoque: {medicamentoSelecionado.estoque} unidades
                        </div>
                      </div>
                    ) : (
                      <div style={{ flex: 1, color: '#6c757d' }}>
                        Nenhum medicamento selecionado
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={abrirModalMedicamentos}
                      className="btn btn-primary"
                    >
                      {medicamentoSelecionado ? 'Alterar' : 'Selecionar'}
                    </button>
                  </div>
                </div>
              )}
              
              {modalMode === 'edit' && medicamentoSelecionado && (
                <div className="form-group">
                  <label>Medicamento</label>
                  <div style={{ 
                    padding: '12px', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ fontWeight: 'bold', color: '#495057' }}>
                      {medicamentoSelecionado.nome}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      {medicamentoSelecionado.principioAtivo} - {medicamentoSelecionado.fabricante}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Quantidade Mensal (unidades) *</label>
                  <input
                    type="number"
                    value={formData.quantidade_mensal}
                    onChange={(e) => setFormData({...formData, quantidade_mensal: e.target.value})}
                    className="form-control"
                    min="1"
                    step="1"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Dosagem Prescrita</label>
                  <input
                    type="text"
                    value={formData.dosagem_prescrita}
                    onChange={(e) => setFormData({...formData, dosagem_prescrita: e.target.value})}
                    className="form-control"
                    placeholder="Ex: 1 comprimido 2x/dia"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Tipo de Retirada *</label>
                <select
                  value={formData.tipo_retirada}
                  onChange={(e) => setFormData({...formData, tipo_retirada: e.target.value, frequencia_semanas: '', dia_retirada: '', intervalo_meses: '1'})}
                  className="form-control"
                  required
                >
                  <option value="mensal">Por mês</option>
                  <option value="semanal">Semanal</option>
                </select>
              </div>

              {formData.tipo_retirada === 'semanal' && (
                <div className="form-group">
                  <label>Frequência (semanas) *</label>
                  <select
                    value={formData.frequencia_semanas}
                    onChange={(e) => setFormData({...formData, frequencia_semanas: e.target.value})}
                    className="form-control"
                    required
                  >
                    <option value="">Selecione a frequência</option>
                    <option value="1">A cada semana</option>
                    <option value="2">A cada 2 semanas</option>
                    <option value="3">A cada 3 semanas</option>
                    <option value="4">A cada 4 semanas</option>
                    <option value="6">A cada 6 semanas</option>
                    <option value="8">A cada 8 semanas</option>
                    <option value="12">A cada 12 semanas</option>
                  </select>
                </div>
              )}

              {formData.tipo_retirada === 'mensal' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Dia da retirada (1-31) *</label>
                    <input
                      type="number"
                      value={formData.dia_retirada}
                      onChange={(e) => setFormData({...formData, dia_retirada: e.target.value})}
                      className="form-control"
                      min="1"
                      max="31"
                      placeholder="Ex: 15 (dia 15 de cada mês)"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Frequência (meses) *</label>
                    <select
                      value={formData.intervalo_meses}
                      onChange={(e) => setFormData({...formData, intervalo_meses: e.target.value})}
                      className="form-control"
                      required
                    >
                      <option value="1">Todo mês</option>
                      <option value="2">A cada 2 meses</option>
                      <option value="3">A cada 3 meses</option>
                      <option value="6">A cada 6 meses</option>
                      <option value="12">Uma vez por ano</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="form-control"
                  rows="3"
                  placeholder="Observações sobre o uso do medicamento..."
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={fecharModal} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={18} />
                  {modalMode === 'add' ? 'Vincular' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Medicamentos */}
      <SeletorMedicamentos
        isOpen={showModalMedicamentos}
        onClose={() => setShowModalMedicamentos(false)}
        onSelect={selecionarMedicamento}
        medicamentosSelecionados={medicamentosVinculados.map(v => ({ id: v.medicamentoRegistrado.id }))}
      />

      {/* Modal de Confirmação de Exclusão */}
      {showModalExclusao && vinculacaoParaExcluir && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 style={{ color: '#dc3545', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Trash2 size={24} />
                Confirmar Exclusão
              </h3>
              <button onClick={fecharModalExclusao} className="modal-close">
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  backgroundColor: '#f8d7da', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 20px' 
                }}>
                  <Trash2 size={40} color="#dc3545" />
                </div>
                
                <h4 style={{ color: '#333', marginBottom: '10px' }}>
                  Remover Medicamento
                </h4>
                
                <p style={{ color: '#667', fontSize: '16px', lineHeight: '1.5', marginBottom: '20px' }}>
                  Você tem certeza que deseja remover <strong>"{vinculacaoParaExcluir.medicamentoRegistrado.nome}"</strong> da lista de medicamentos deste paciente?
                </p>
                
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '6px',
                  padding: '15px',
                  fontSize: '14px',
                  color: '#856404',
                  textAlign: 'left'
                }}>
                  <strong>⚠️ Atenção:</strong> Esta ação não pode ser desfeita. O medicamento será removido da lista ativa, mas o histórico de modificações será preservado para auditoria.
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={fecharModalExclusao} 
                  className="btn btn-secondary"
                  style={{ marginRight: '10px' }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  onClick={confirmarExclusao} 
                  className="btn btn-danger"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Trash2 size={18} />
                  Confirmar Exclusão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PacienteMedicamentos;